import { useState } from "react";
import { Download, Lock, Loader2, LogOut, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { isTestDevice, setTestDevice } from "../lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL;

// Minimal admin panel for the beta-user CSV export.
// Visibility: only when the URL contains ?admin=1 (or ?admin=true).
// Auth: ADMIN_PASSWORD from backend/.env. Sent as X-Admin-Password header on
// every request — we never store the password in localStorage to avoid leaking
// it from a stolen device. It only lives in React state.
export default function AdminPanel() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/diagnostics/count`, {
        headers: { "X-Admin-Password": password },
      });
      if (res.status === 401) {
        setError("Contraseña incorrecta");
        return;
      }
      if (!res.ok) {
        // Surface the actual HTTP status so we can diagnose at a glance:
        //   404 → backend doesn't have the admin endpoints (likely producción sin deploy)
        //   5xx → backend crashed / DB connection failed
        //   anything else → unexpected response shape
        let extra = "";
        try {
          const body = await res.text();
          if (body) extra = ` · ${body.slice(0, 120)}`;
        } catch { /* body unreadable, ignore */ }
        setError(
          `Error del servidor (HTTP ${res.status}). ` +
          (res.status === 404
            ? "El endpoint no existe — probablemente necesitas re-deployar a producción."
            : res.status >= 500
              ? "El backend tuvo un fallo interno. Revisa los logs."
              : "Respuesta inesperada del servidor.") +
          extra,
        );
        return;
      }
      const data = await res.json();
      setTotal(data.total);
      setAuthed(true);
      // After login, fetch analytics summary in parallel — best-effort.
      fetchAnalytics(password);
    } catch (err) {
      const msg = err && err.message ? err.message : "desconocido";
      setError(`No pudimos conectar con el servidor (${msg}). Revisa tu internet o el CORS.`);
    } finally {
      setLoading(false);
    }
  };

  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [testDevice, setTestDeviceState] = useState(() => isTestDevice());

  const toggleTestDevice = () => {
    const next = !testDevice;
    setTestDevice(next);
    setTestDeviceState(next);
    toast.success(
      next
        ? "Este navegador quedó marcado como de prueba. Tus eventos se excluirán."
        : "Se quitó la marca de prueba de este navegador.",
      { duration: 2500 },
    );
  };

  const fetchAnalytics = async (pwd) => {
    setAnalyticsLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/analytics/summary`, {
        headers: { "X-Admin-Password": pwd || password },
      });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch {
      /* non-critical */
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const downloadCsv = async (path, fallbackName) => {
    try {
      const res = await fetch(`${API}${path}`, {
        headers: { "X-Admin-Password": password },
      });
      if (!res.ok) {
        toast.error("No se pudo descargar el CSV.");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `${fallbackName}-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch {
      toast.error("Error de red al descargar.");
    }
  };

  const refreshCount = async () => {
    try {
      const res = await fetch(`${API}/api/admin/diagnostics/count`, {
        headers: { "X-Admin-Password": password },
      });
      if (res.ok) {
        const data = await res.json();
        setTotal(data.total);
      }
    } catch {
      /* swallow — non-critical refresh */
    }
  };

  // Re-fetch the count whenever the panel mounts in "authed" state so the admin
  // doesn't have to refresh the page to see new entries. We deliberately skip
  // useEffect here (we already set total on login) and rely on the manual
  // "Actualizar contador" button below for subsequent refreshes.

  const handleDownload = async () => {
    // Legacy single-button download — kept for backwards-compat behaviour but now
    // delegates to the shared helper. Used by the primary "Descargar CSV
    // (diagnósticos)" button via `downloadCsv` instead.
    setDownloading(true);
    await downloadCsv("/api/admin/diagnostics.csv", "negoia-diagnostics");
    setDownloading(false);
  };

  const logout = () => {
    setAuthed(false);
    setPassword("");
    setTotal(null);
  };

  if (!authed) {
    return (
      <form
        onSubmit={handleLogin}
        className="flex flex-col min-h-[100dvh] px-6 pt-12 pb-10"
        data-testid="admin-login"
      >
        <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-900 text-orange-400 self-start mb-5">
          <Lock className="w-6 h-6" strokeWidth={2.4} />
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
          Panel admin <span className="text-orange-500">NegoIA</span>
        </h1>
        <p className="text-sm text-slate-500 mt-2 leading-relaxed">
          Acceso restringido — necesitas la contraseña de administrador para descargar las respuestas de los usuarios beta.
        </p>

        <label className="flex flex-col gap-2 mt-8">
          <span className="text-sm font-semibold text-slate-700">Contraseña</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            data-testid="admin-password-input"
            placeholder="••••••••"
            className="w-full p-4 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-900 text-base focus:border-orange-500 focus:bg-white outline-none transition-all"
          />
        </label>

        {error && (
          <p className="text-sm font-semibold text-red-600 mt-3" data-testid="admin-login-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading || !password.trim()}
          data-testid="admin-login-button"
          className={[
            "mt-6 w-full inline-flex items-center justify-center gap-2 py-4 rounded-2xl text-lg font-bold transition-all duration-200",
            loading || !password.trim()
              ? "bg-slate-100 text-slate-400 cursor-not-allowed"
              : "bg-orange-500 text-white shadow-lg shadow-orange-500/30 active:scale-[0.98] active:bg-orange-600",
          ].join(" ")}
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5} />
              Validando...
            </>
          ) : (
            "Entrar"
          )}
        </button>
      </form>
    );
  }

  return (
    <div className="flex flex-col min-h-[100dvh] px-6 pt-10 pb-10" data-testid="admin-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Panel admin</p>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">NegoIA</h1>
        </div>
        <button
          type="button"
          onClick={logout}
          data-testid="admin-logout-button"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-slate-600 bg-slate-100 active:bg-slate-200"
        >
          <LogOut className="w-4 h-4" strokeWidth={2.4} />
          Salir
        </button>
      </div>

      <div className="rounded-2xl p-6 bg-slate-900 text-white" data-testid="admin-stats-card">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-400">
          Diagnósticos guardados
        </p>
        <p className="text-5xl font-extrabold mt-2" data-testid="admin-total">
          {total ?? "—"}
        </p>
        <p className="text-xs text-slate-400 mt-2">
          Total acumulado de usuarios beta que completaron el diagnóstico de 12 preguntas.
        </p>
      </div>

      <button
        type="button"
        onClick={() => { setDownloading(true); downloadCsv("/api/admin/diagnostics.csv", "negoia-diagnostics").finally(() => setDownloading(false)); }}
        disabled={downloading || (total === 0)}
        data-testid="admin-download-csv-button"
        className={[
          "mt-6 w-full inline-flex items-center justify-center gap-2 py-4 rounded-2xl text-lg font-bold transition-all duration-200",
          downloading || total === 0
            ? "bg-slate-100 text-slate-400 cursor-not-allowed"
            : "bg-orange-500 text-white shadow-lg shadow-orange-500/30 active:scale-[0.98] active:bg-orange-600",
        ].join(" ")}
      >
        {downloading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5} />
            Descargando...
          </>
        ) : (
          <>
            <Download className="w-5 h-5" strokeWidth={2.5} />
            Descargar CSV (diagnósticos)
          </>
        )}
      </button>

      <button
        type="button"
        onClick={refreshCount}
        data-testid="admin-refresh-button"
        className="mt-3 inline-flex items-center justify-center gap-2 text-sm font-semibold text-slate-500 active:text-slate-700 py-2"
      >
        Actualizar contador
      </button>

      <p className="text-xs text-slate-400 mt-6 leading-relaxed">
        El archivo CSV incluye: fecha, nombre, WhatsApp, consentimiento, giro, todas las respuestas P1–P12, semáforos calculados, ciudad y user agent.
      </p>

      {/* ─── Analytics block ─────────────────────────────────────────────── */}
      <div className="mt-10 pt-8 border-t-2 border-dashed border-slate-200" data-testid="admin-analytics-section">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">Analítica</p>
        <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Tráfico y eventos</h2>

        {analyticsLoading && !analytics && (
          <p className="text-sm text-slate-400 mt-3">Cargando...</p>
        )}

        {analytics && (
          <>
            <div className="grid grid-cols-2 gap-3 mt-5" data-testid="admin-analytics-grid">
              <StatTile label="Visitas totales" value={analytics.total_visits} testid="stat-total-visits" />
              <StatTile label="Sesiones únicas" value={analytics.unique_sessions} testid="stat-unique-sessions" />
              <StatTile label="Visitas hoy" value={analytics.visits_today} testid="stat-visits-today" />
              <StatTile label="Clics a WhatsApp" value={analytics.whatsapp_clicks} testid="stat-whatsapp-clicks" highlight />
            </div>

            {analytics.top_cities && analytics.top_cities.length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Top ciudades
                </p>
                <ul className="flex flex-col gap-1.5" data-testid="admin-top-cities">
                  {analytics.top_cities.map((c) => (
                    <li
                      key={c.location}
                      className="flex justify-between text-sm text-slate-700 bg-slate-50 px-3 py-2 rounded-lg"
                    >
                      <span className="font-semibold">{c.location}</span>
                      <span className="text-slate-500">{c.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {analytics.events_by_name && analytics.events_by_name.length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Eventos por tipo
                </p>
                <ul className="flex flex-col gap-1.5" data-testid="admin-events-by-name">
                  {analytics.events_by_name.map((e) => (
                    <li
                      key={e.name}
                      className="flex justify-between text-sm text-slate-700 bg-slate-50 px-3 py-2 rounded-lg"
                    >
                      <span className="font-mono text-xs">{e.name}</span>
                      <span className="text-slate-500">{e.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* a) Top giros más seleccionados */}
            <div className="mt-6" data-testid="admin-top-giros-block">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                Top giros más seleccionados
              </p>
              {analytics.top_giros && analytics.top_giros.length > 0 ? (
                <ul className="flex flex-col gap-1.5" data-testid="admin-top-giros">
                  {analytics.top_giros.map((g) => (
                    <li
                      key={g.giro}
                      className="flex justify-between text-sm text-slate-700 bg-slate-50 px-3 py-2 rounded-lg"
                    >
                      <span className="font-semibold capitalize">{g.giro}</span>
                      <span className="text-slate-500">{g.count}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-400" data-testid="admin-top-giros-empty">
                  Aún no hay selecciones de giro registradas.
                </p>
              )}
            </div>

            {/* b) Top campañas UTM — visitas y eventos por campaña */}
            <div className="mt-6" data-testid="admin-top-campaigns-block">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                Top campañas UTM
              </p>
              {analytics.top_campaigns && analytics.top_campaigns.length > 0 ? (
                <ul className="flex flex-col gap-2.5" data-testid="admin-top-campaigns">
                  {analytics.top_campaigns.map((c) => (
                    <li
                      key={c.campaign}
                      className="bg-slate-50 rounded-xl p-3 border border-slate-100"
                      data-testid={`admin-campaign-${c.campaign}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-slate-900 truncate pr-2">{c.campaign}</span>
                        <span
                          className="shrink-0 text-xs font-extrabold px-2 py-1 rounded-full bg-green-100 text-green-700"
                          data-testid={`admin-campaign-rate-${c.campaign}`}
                        >
                          {(c.conversion_rate ?? 0)}% conv.
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs">
                        <span className="text-slate-500">
                          Visitas <span className="font-bold text-teal-700">{c.visits}</span>
                        </span>
                        <span className="text-slate-500">
                          Conversiones <span className="font-bold text-green-700">{c.conversions ?? 0}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-700 bg-orange-50 px-2 py-1 rounded-md">
                          Plan $49: {c.conv_plan_49 ?? 0}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 bg-slate-200/60 px-2 py-1 rounded-md">
                          Consulta WA: {c.conv_wa_consult ?? 0}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-400" data-testid="admin-top-campaigns-empty">
                  Aún no llegan visitas con parámetros UTM (utm_campaign).
                </p>
              )}
            </div>
          </>
        )}

        <div className="flex flex-col gap-2 mt-6">
          <div
            className={[
              "rounded-xl p-4 border-2",
              testDevice ? "bg-teal-50 border-teal-200" : "bg-slate-50 border-slate-200",
            ].join(" ")}
            data-testid="test-device-card"
          >
            <div className="flex items-start gap-2">
              {testDevice ? (
                <ShieldCheck className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" strokeWidth={2.4} />
              ) : (
                <ShieldOff className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" strokeWidth={2.4} />
              )}
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-900 leading-snug">
                  {testDevice
                    ? "Este navegador está marcado como de prueba"
                    : "Marcar este navegador como de prueba"}
                </p>
                <p className="text-xs text-slate-500 mt-1 leading-snug">
                  Excluye tus propios eventos de las estadísticas en este navegador, aunque no abras el panel admin.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={toggleTestDevice}
              data-testid="toggle-test-device-button"
              className={[
                "mt-3 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all active:scale-[0.98]",
                testDevice
                  ? "bg-white text-slate-700 border-2 border-slate-200"
                  : "bg-teal-600 text-white",
              ].join(" ")}
            >
              {testDevice ? "Quitar marca de prueba" : "Marcar como de prueba"}
            </button>
          </div>

          <button
            type="button"
            onClick={() => downloadCsv("/api/admin/analytics/visits.csv", "negoia-visits")}
            data-testid="admin-download-visits-csv"
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold bg-slate-900 text-white active:scale-[0.98] transition-all"
          >
            <Download className="w-4 h-4" strokeWidth={2.5} />
            Descargar CSV de visitas
          </button>
          <button
            type="button"
            onClick={() => downloadCsv("/api/admin/analytics/events.csv", "negoia-events")}
            data-testid="admin-download-events-csv"
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold bg-slate-900 text-white active:scale-[0.98] transition-all"
          >
            <Download className="w-4 h-4" strokeWidth={2.5} />
            Descargar CSV de eventos
          </button>
          <button
            type="button"
            onClick={() => fetchAnalytics()}
            data-testid="admin-refresh-analytics"
            className="inline-flex items-center justify-center gap-2 text-xs font-semibold text-slate-500 active:text-slate-700 py-2"
          >
            Actualizar analítica
          </button>
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, testid, highlight }) {
  return (
    <div
      className={[
        "rounded-xl p-4",
        highlight ? "bg-orange-50 border-2 border-orange-200" : "bg-slate-50 border-2 border-slate-100",
      ].join(" ")}
      data-testid={testid}
    >
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 leading-snug">
        {label}
      </p>
      <p
        className={[
          "text-3xl font-extrabold mt-1",
          highlight ? "text-orange-600" : "text-slate-900",
        ].join(" ")}
      >
        {value ?? "—"}
      </p>
    </div>
  );
}
